import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Box, Button, Checkbox, Chip, CircularProgress, FormControl, FormControlLabel,
    IconButton, InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import GroupIcon from '@mui/icons-material/Group';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import fileUrl from '../../utils/fileUrl';
import {
    getEvent, saveEvent, publishEvent, previewAudience,
    uploadEventBanner, uploadEventMaterial,
} from '../../api/eventApi';
import { getAllRoles } from '../../api/roleRightApi';
import { getAllAgents, getAllAgentGroups } from '../../api/userApi';
import EventAttendance from './eventAttendance';

const PP = "'Poppins', sans-serif";

// Same shell the Tutorial form uses, so the two read as one system.
const cardSx = { border: '1px solid #E2E8F0', borderRadius: '12px', p: { xs: 2, md: 2.5 }, bgcolor: '#fff' };
const cardHeadSx = {
    fontSize: '0.8rem', fontWeight: 700, color: '#0F172A', fontFamily: PP,
    mb: 2, pb: 1, borderBottom: '1px solid #F1F5F9', letterSpacing: '0.2px',
};
const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: PP } };

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const AUDIENCE_TYPES = [
    { value: 'User', label: 'A named person' },
    { value: 'Role', label: 'A role' },
    { value: 'Group', label: 'A team' },
    { value: 'NewJoiner', label: 'New joiners (last N days)' },
    { value: 'Downline', label: "A leader's downline" },
    { value: 'Ranking', label: 'Top N by sales' },
    { value: 'SalesRevenue', label: 'Lifetime revenue over (RM)' },
    { value: 'SalesCount', label: 'Lifetime cases over' },
];

// What people actually say, mapped onto the two shapes the server understands.
const REPEATS = [
    { key: 'none', label: 'One time', type: 'None' },
    { key: 'weekly', label: 'Every week', type: 'Weekly', interval: 1 },
    { key: 'fortnightly', label: 'Every 2 weeks', type: 'Weekly', interval: 2 },
    { key: 'twice', label: 'Twice a month', type: 'Monthly', weeks: '1,3' },
    { key: 'monthly', label: 'Once a month', type: 'Monthly', weeks: '2' },
];
const WEEK_LABELS = [
    { v: '1', label: '1st' }, { v: '2', label: '2nd' }, { v: '3', label: '3rd' },
    { v: '4', label: '4th' }, { v: '5', label: 'Last' },
];

/** Which preset a saved event matches, so editing reopens on the right one. */
function repeatKeyOf(f) {
    if (f.recurrenceType === 'Weekly') return (f.recurrenceInterval || 1) >= 2 ? 'fortnightly' : 'weekly';
    if (f.recurrenceType === 'Monthly') {
        const n = (f.recurrenceWeeks || '').split(',').filter(Boolean).length;
        return n > 1 ? 'twice' : 'monthly';
    }
    return 'none';
}

const EMPTY = {
    eventId: 0, title: '', type: 'Training', description: '',
    videoUrl: '', isLive: false, clockSynced: true,
    isOnline: true, venue: '', venueMapUrl: '',
    visibility: 'Public', requiresApproval: false, capacity: '', showAttendeeNames: true,
    recurrenceType: 'None', recurrenceDay: 1, recurrenceTime: '20:00',
    recurrenceInterval: 1, recurrenceWeeks: '1,3',
    recurrenceDurationMinutes: 60, recurrenceUntil: '',
    aiReplyEnabled: false, attachments: '', bannerUrl: '',
    audience: [], sessions: [],
};

/* ============================================================================
   CREATE / EDIT AN EVENT — a page, not a dialog

   A dialog was the wrong shape for this: the form carries five sections and a
   repeating audience editor, so it scrolled inside a box inside the page and the
   Save button sat below the fold. (user 2026-10-03: "create event in new page
   instead of popup", "dont allow like that popup")

   Styled off the Tutorial form - the same card shell, headings and field
   rounding - so the two read as one system rather than two.
   ========================================================================= */
export default function EventFormPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();            // undefined on /events/new
    const isEdit = Boolean(id);

    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [preview, setPreview] = useState(null);
    // The sessions as the SERVER sees them (with their counts), kept apart from the
    // editable list in `form` - attendance reads the real ones, not the draft.
    const [serverSessions, setServerSessions] = useState([]);
    const [uploading, setUploading] = useState('');
    const bannerRef = useRef(null);
    const materialRef = useRef(null);

    const [roles, setRoles] = useState([]);
    const [groups, setGroups] = useState([]);
    const [agents, setAgents] = useState([]);

    useEffect(() => {
        getAllRoles().then(r => setRoles(r.data || [])).catch(() => {});
        getAllAgentGroups().then(r => setGroups(r.data || [])).catch(() => {});
        getAllAgents().then(r => setAgents(r.data || [])).catch(() => {});
    }, []);

    const load = useCallback(async () => {
        if (!isEdit) return;
        setLoading(true);
        try {
            const res = await getEvent(id);
            const e = res.data;
            setServerSessions(e.sessions || []);
            setForm({
                ...EMPTY,
                ...e,
                capacity: e.capacity ?? '',
                recurrenceDay: e.recurrenceDay ?? 1,
                recurrenceInterval: e.recurrenceInterval ?? 1,
                recurrenceWeeks: e.recurrenceWeeks || '1,3',
                recurrenceTime: e.recurrenceTime || '20:00',
                recurrenceDurationMinutes: e.recurrenceDurationMinutes || 60,
                recurrenceUntil: e.recurrenceUntil ? e.recurrenceUntil.slice(0, 10) : '',
                audience: e.audience || [],
                sessions: (e.sessions || []).map(s => ({
                    eventSessionId: s.eventSessionId,
                    startsOn: s.startsOn?.slice(0, 16) || '',
                    endsOn: s.endsOn?.slice(0, 16) || '',
                    topic: s.topic || '',
                    status: s.status,
                })),
            });
        } catch {
            setError('Could not load that event.');
        } finally {
            setLoading(false);
        }
    }, [id, isEdit]);

    useEffect(() => { load(); }, [load]);

    /* #sessions lands on the session list rather than the top of a long form. The scroll
       waits for `loading` because the panel does not exist until the event has arrived —
       React Router does not restore anchors by itself, and scrolling to an element that
       has not rendered yet does nothing at all. (user 2026-10-19) */
    useEffect(() => {
        if (loading || location.hash !== '#sessions') return;
        const el = document.getElementById('sessions');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [loading, location.hash]);

    const payload = useMemo(() => ({
        ...form,
        capacity: form.capacity === '' ? null : Number(form.capacity),
        recurrenceUntil: form.recurrenceUntil || null,
        audience: (form.audience || [])
            .filter(a => a.audienceType && a.refId !== '' && a.refId != null)
            .map(a => ({ ...a, refId: Number(a.refId) })),
        sessions: (form.sessions || []).filter(s => s.startsOn && s.endsOn),
    }), [form]);

    const set = (patch) => setForm(f => ({ ...f, ...patch }));

    // Material is stored as JSON on the event; the form keeps it as a list so nobody
    // has to hand-write it. (user 2026-10-03: "upload material instead of this")
    const material = useMemo(() => {
        if (!form.attachments) return [];
        try {
            const parsed = JSON.parse(form.attachments);
            return Array.isArray(parsed) ? parsed.filter(a => a?.url) : [];
        } catch { return []; }
    }, [form.attachments]);
    const setMaterial = (list) => set({ attachments: list.length ? JSON.stringify(list) : '' });

    const pickRepeat = (key) => {
        const r = REPEATS.find(x => x.key === key) || REPEATS[0];
        set({
            recurrenceType: r.type,
            recurrenceInterval: r.interval ?? form.recurrenceInterval ?? 1,
            recurrenceWeeks: r.weeks ?? form.recurrenceWeeks ?? '1,3',
        });
    };

    const toggleWeek = (v) => {
        const have = (form.recurrenceWeeks || '').split(',').filter(Boolean);
        const next = have.includes(v) ? have.filter(x => x !== v) : [...have, v];
        next.sort();
        set({ recurrenceWeeks: next.join(',') });
    };

    const onBanner = async (file) => {
        if (!file) return;
        setUploading('banner');
        setError('');
        try {
            const res = await uploadEventBanner(file);
            set({ bannerUrl: res.data.url });
        } catch (e) {
            setError(e?.response?.data?.message || 'Could not upload that banner.');
        } finally { setUploading(''); }
    };

    const onMaterial = async (file) => {
        if (!file) return;
        setUploading('material');
        setError('');
        try {
            const res = await uploadEventMaterial(file);
            setMaterial([...material, { url: res.data.url, name: res.data.fileName }]);
        } catch (e) {
            setError(e?.response?.data?.message || 'Could not upload that file.');
        } finally { setUploading(''); }
    };

    const save = async ({ thenPublish = false } = {}) => {
        if (!form.title.trim()) { setError('Give the event a title.'); return; }
        setSaving(true);
        setError('');
        try {
            const res = await saveEvent(payload);
            const savedId = res.data?.eventId;
            if (thenPublish && savedId) {
                try {
                    const pub = await publishEvent(savedId);
                    setNotice(pub.data?.message || 'Published.');
                } catch (e) {
                    setError(e?.response?.data?.message || 'Saved, but could not publish.');
                    setSaving(false);
                    return;
                }
            }
            navigate('/events/manage');
        } catch (e) {
            setError(e?.response?.data?.message || 'Could not save that.');
        } finally {
            setSaving(false);
        }
    };

    const runPreview = async () => {
        try {
            const res = await previewAudience(payload);
            setPreview(res.data);
        } catch {
            setError('Could not work out the audience.');
        }
    };

    // ── audience rows ───────────────────────────────────────────────────────
    const setRule = (i, patch) => setForm(f => ({
        ...f, audience: f.audience.map((a, j) => (j === i ? { ...a, ...patch } : a)),
    }));
    const addRule = () => setForm(f => ({
        ...f, audience: [...(f.audience || []), { audienceType: 'User', refId: '', isExcluded: false }],
    }));
    const dropRule = (i) => setForm(f => ({ ...f, audience: f.audience.filter((_, j) => j !== i) }));

    const RefField = ({ rule, i }) => {
        const common = { size: 'small', fullWidth: true, sx: fieldSx };
        if (rule.audienceType === 'Role') {
            return (
                <FormControl {...common}>
                    <InputLabel sx={{ fontFamily: PP }}>Role</InputLabel>
                    <Select label="Role" value={rule.refId} onChange={e => setRule(i, { refId: e.target.value })}
                        sx={{ fontFamily: PP }}>
                        {roles.map(r => <MenuItem key={r.roleId} value={r.roleId} sx={{ fontFamily: PP }}>{r.roleName}</MenuItem>)}
                    </Select>
                </FormControl>
            );
        }
        if (rule.audienceType === 'Group') {
            return (
                <FormControl {...common}>
                    <InputLabel sx={{ fontFamily: PP }}>Team</InputLabel>
                    <Select label="Team" value={rule.refId} onChange={e => setRule(i, { refId: e.target.value })}
                        sx={{ fontFamily: PP }}>
                        {groups.map(g => <MenuItem key={g.groupId} value={g.groupId} sx={{ fontFamily: PP }}>{g.groupName}</MenuItem>)}
                    </Select>
                </FormControl>
            );
        }
        if (rule.audienceType === 'User' || rule.audienceType === 'Downline') {
            return (
                <FormControl {...common}>
                    <InputLabel sx={{ fontFamily: PP }}>Person</InputLabel>
                    <Select label="Person" value={rule.refId} onChange={e => setRule(i, { refId: e.target.value })}
                        sx={{ fontFamily: PP }}>
                        {agents.map(a => (
                            <MenuItem key={a.userId} value={a.userId} sx={{ fontFamily: PP }}>
                                {`${a.lastName || ''} ${a.firstName || ''}`.trim() || a.username}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }
        const label = rule.audienceType === 'NewJoiner' ? 'Days'
            : rule.audienceType === 'Ranking' ? 'Top N'
                : rule.audienceType === 'SalesRevenue' ? 'RM' : 'Cases';
        return <TextField {...common} type="number" label={label} value={rule.refId}
            onChange={e => setRule(i, { refId: e.target.value })} />;
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={32} /></Box>;
    }

    return (
        <Box sx={{ width: '100%', fontFamily: PP, pb: 6 }}>
            {/* header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <IconButton size="small" onClick={() => navigate('/events/manage')}
                    sx={{ color: '#64748B', border: '1px solid #E2E8F0', borderRadius: '8px', p: 0.75 }}>
                    <ArrowBackIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Box>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, lineHeight: 1.2 }}>
                        {isEdit ? 'Edit Event' : 'Create Event'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#64748B', fontFamily: PP }}>
                        {isEdit ? `Editing event #${id}` : 'Build it, choose who it reaches, then publish'}
                    </Typography>
                </Box>
            </Box>

            {error && (
                <Box sx={{ bgcolor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', p: 1.5, mb: 2 }}>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#DC2626' }}>{error}</Typography>
                </Box>
            )}
            {notice && (
                <Box sx={{ bgcolor: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '8px', p: 1.5, mb: 2 }}>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#065F46' }}>{notice}</Typography>
                </Box>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>

                {/* ── Basics ─────────────────────────────────────────────── */}
                <Paper elevation={0} sx={{ ...cardSx, gridColumn: '1 / -1' }}>
                    <Typography sx={cardHeadSx}>Basics</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2 }}>
                        <TextField size="small" label="Title" sx={fieldSx} value={form.title}
                            onChange={e => set({ title: e.target.value })} inputProps={{ maxLength: 200 }} />
                        <FormControl size="small" sx={fieldSx}>
                            <InputLabel sx={{ fontFamily: PP }}>Type</InputLabel>
                            <Select label="Type" value={form.type} onChange={e => set({ type: e.target.value })}
                                sx={{ fontFamily: PP }}>
                                <MenuItem value="Training" sx={{ fontFamily: PP }}>Training</MenuItem>
                                <MenuItem value="Event" sx={{ fontFamily: PP }}>Event</MenuItem>
                                <MenuItem value="Travel" sx={{ fontFamily: PP }}>Travel</MenuItem>
                                <MenuItem value="Recruitment" sx={{ fontFamily: PP }}>Recruitment</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField size="small" label="Description" multiline minRows={3} sx={{ ...fieldSx, gridColumn: '1 / -1' }}
                            value={form.description} onChange={e => set({ description: e.target.value })} />
                    </Box>
                </Paper>

                {/* ── Where ──────────────────────────────────────────────── */}
                <Paper elevation={0} sx={cardSx}>
                    <Typography sx={cardHeadSx}>Where</Typography>
                    <Stack spacing={2}>
                        <FormControlLabel
                            control={<Checkbox checked={form.isOnline} onChange={e => set({ isOnline: e.target.checked })} />}
                            label={<Typography sx={{ fontFamily: PP, fontSize: '0.84rem' }}>Online session</Typography>} />
                        {form.isOnline ? (
                            <>
                                <TextField size="small" label="Video link (YouTube)" sx={fieldSx}
                                    value={form.videoUrl || ''} onChange={e => set({ videoUrl: e.target.value })} />
                                <FormControlLabel
                                    control={<Checkbox checked={form.clockSynced} onChange={e => set({ clockSynced: e.target.checked })} />}
                                    label={<Typography sx={{ fontFamily: PP, fontSize: '0.84rem' }}>
                                        Everyone watches the same moment
                                    </Typography>} />
                                <FormControlLabel
                                    control={<Checkbox checked={form.isLive} onChange={e => set({ isLive: e.target.checked })} />}
                                    label={<Typography sx={{ fontFamily: PP, fontSize: '0.84rem' }}>
                                        This is a YouTube live stream
                                    </Typography>} />
                            </>
                        ) : (
                            <>
                                <TextField size="small" label="Venue" sx={fieldSx}
                                    value={form.venue || ''} onChange={e => set({ venue: e.target.value })} />
                                <TextField size="small" label="Map link" sx={fieldSx}
                                    value={form.venueMapUrl || ''} onChange={e => set({ venueMapUrl: e.target.value })} />
                            </>
                        )}
                    </Stack>
                </Paper>

                {/* ── When ───────────────────────────────────────────────── */}
                <Paper elevation={0} sx={cardSx}>
                    <Typography sx={cardHeadSx}>When</Typography>
                    <Stack spacing={2}>
                        <FormControl size="small" sx={fieldSx}>
                            <InputLabel sx={{ fontFamily: PP }}>Repeats</InputLabel>
                            <Select label="Repeats" value={repeatKeyOf(form)}
                                onChange={e => pickRepeat(e.target.value)} sx={{ fontFamily: PP }}>
                                {REPEATS.map(r => (
                                    <MenuItem key={r.key} value={r.key} sx={{ fontFamily: PP }}>{r.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {form.recurrenceType === 'Monthly' && (
                            <Box>
                                <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#475569', mb: 0.75 }}>
                                    Which {DAYS[form.recurrenceDay]}s of the month?
                                </Typography>
                                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                                    {WEEK_LABELS.map(w => {
                                        const on = (form.recurrenceWeeks || '').split(',').includes(w.v);
                                        return (
                                            <Chip key={w.v} label={w.label} size="small" clickable
                                                onClick={() => toggleWeek(w.v)}
                                                sx={{
                                                    fontFamily: PP, fontWeight: 700, fontSize: '0.72rem',
                                                    bgcolor: on ? '#0E8FD1' : '#F1F5F9',
                                                    color: on ? '#fff' : '#475569',
                                                    '&:hover': { bgcolor: on ? '#0B7AB4' : '#E2E8F0' },
                                                }} />
                                        );
                                    })}
                                </Stack>
                                <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#64748B', mt: 0.75 }}>
                                    "Last" is the final one in the month, so it still works where there are only four.
                                </Typography>
                            </Box>
                        )}

                        {form.recurrenceType !== 'None' ? (
                            <>
                                <Stack direction="row" spacing={1.5}>
                                    <FormControl size="small" fullWidth sx={fieldSx}>
                                        <InputLabel sx={{ fontFamily: PP }}>Day</InputLabel>
                                        <Select label="Day" value={form.recurrenceDay}
                                            onChange={e => set({ recurrenceDay: e.target.value })} sx={{ fontFamily: PP }}>
                                            {DAYS.map((d, i) => <MenuItem key={i} value={i} sx={{ fontFamily: PP }}>{d}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <TextField size="small" fullWidth type="time" label="Start" sx={fieldSx}
                                        InputLabelProps={{ shrink: true }}
                                        value={form.recurrenceTime} onChange={e => set({ recurrenceTime: e.target.value })} />
                                    <TextField size="small" fullWidth type="number" label="Minutes" sx={fieldSx}
                                        value={form.recurrenceDurationMinutes}
                                        onChange={e => set({ recurrenceDurationMinutes: e.target.value })} />
                                </Stack>
                                <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#64748B' }}>
                                    The next dozen dates fill in on save, and a date that already has a session is
                                    left alone.
                                </Typography>
                            </>
                        ) : (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                <TextField size="small" fullWidth type="datetime-local" label="Starts" sx={fieldSx}
                                    InputLabelProps={{ shrink: true }}
                                    value={form.sessions?.[0]?.startsOn || ''}
                                    onChange={e => set({ sessions: [{ ...(form.sessions?.[0] || { eventSessionId: 0 }), startsOn: e.target.value }] })} />
                                <TextField size="small" fullWidth type="datetime-local" label="Ends" sx={fieldSx}
                                    InputLabelProps={{ shrink: true }}
                                    value={form.sessions?.[0]?.endsOn || ''}
                                    onChange={e => set({ sessions: [{ ...(form.sessions?.[0] || { eventSessionId: 0 }), endsOn: e.target.value }] })} />
                            </Stack>
                        )}
                    </Stack>
                </Paper>

                {/* ── Who ────────────────────────────────────────────────── */}
                <Paper elevation={0} sx={{ ...cardSx, gridColumn: '1 / -1' }}>
                    <Typography sx={cardHeadSx}>Who it reaches</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2, mb: 2 }}>
                        <FormControl size="small" sx={fieldSx}>
                            <InputLabel sx={{ fontFamily: PP }}>Visibility</InputLabel>
                            <Select label="Visibility" value={form.visibility}
                                onChange={e => set({ visibility: e.target.value })} sx={{ fontFamily: PP }}>
                                <MenuItem value="Public" sx={{ fontFamily: PP }}>Public — everyone sees it all</MenuItem>
                                <MenuItem value="Private" sx={{ fontFamily: PP }}>Private — others see the title only</MenuItem>
                                <MenuItem value="Targeted" sx={{ fontFamily: PP }}>Limited — others never see it</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField size="small" type="number" label="Capacity" placeholder="unlimited" sx={fieldSx}
                            value={form.capacity} onChange={e => set({ capacity: e.target.value })} />
                        <FormControlLabel
                            control={<Checkbox checked={form.requiresApproval} onChange={e => set({ requiresApproval: e.target.checked })} />}
                            label={<Typography sx={{ fontFamily: PP, fontSize: '0.84rem' }}>Approve each request</Typography>} />
                    </Box>

                    {form.visibility !== 'Public' && (
                        <>
                            <Typography sx={{ fontFamily: PP, fontSize: '0.76rem', color: '#64748B', mb: 1.25 }}>
                                Rules add up. Tick <b>exclude</b> to take those people back out again.
                            </Typography>
                            <Stack spacing={1.25}>
                                {(form.audience || []).map((rule, i) => (
                                    <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}>
                                        <FormControl size="small" sx={{ ...fieldSx, minWidth: 220 }}>
                                            <InputLabel sx={{ fontFamily: PP }}>Invite</InputLabel>
                                            <Select label="Invite" value={rule.audienceType}
                                                onChange={e => setRule(i, { audienceType: e.target.value, refId: '' })}
                                                sx={{ fontFamily: PP }}>
                                                {AUDIENCE_TYPES.map(t => (
                                                    <MenuItem key={t.value} value={t.value} sx={{ fontFamily: PP }}>{t.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Box sx={{ flex: 1, width: '100%' }}><RefField rule={rule} i={i} /></Box>
                                        <FormControlLabel
                                            control={<Checkbox size="small" checked={!!rule.isExcluded}
                                                onChange={e => setRule(i, { isExcluded: e.target.checked })} />}
                                            label={<Typography sx={{ fontFamily: PP, fontSize: '0.78rem' }}>exclude</Typography>} />
                                        <IconButton size="small" onClick={() => dropRule(i)}>
                                            <DeleteOutlineIcon sx={{ fontSize: 18, color: '#DC2626' }} />
                                        </IconButton>
                                    </Stack>
                                ))}
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                                <Button size="small" startIcon={<AddIcon />} onClick={addRule}
                                    sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem' }}>
                                    Add a rule
                                </Button>
                                <Button size="small" startIcon={<GroupIcon />} onClick={runPreview}
                                    sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem' }}>
                                    Who does this reach?
                                </Button>
                                {preview && (
                                    <Chip size="small" label={`${preview.count} ${preview.count === 1 ? 'person' : 'people'}`}
                                        sx={{
                                            fontFamily: PP, fontWeight: 700,
                                            bgcolor: preview.count ? '#D1FAE5' : '#FEE2E2',
                                            color: preview.count ? '#065F46' : '#991B1B',
                                        }} />
                                )}
                                {preview && preview.count > 0 && preview.count <= 12 && (
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.76rem', color: '#64748B' }}>
                                        {preview.people.map(p => p.name).join(', ')}
                                    </Typography>
                                )}
                            </Stack>
                        </>
                    )}
                </Paper>

                {/* ── Extras ─────────────────────────────────────────────── */}
                <Paper elevation={0} sx={{ ...cardSx, gridColumn: '1 / -1' }}>
                    <Typography sx={cardHeadSx}>Banner &amp; material</Typography>

                    <Stack spacing={2}>
                        <Box>
                            <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', fontWeight: 600, color: '#334155', mb: 0.75 }}>
                                Banner
                            </Typography>
                            <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#64748B', mb: 1 }}>
                                Shown across the top of the event, and on the dashboard for whoever is invited.
                                Wide images work best. Under 5 MB.
                            </Typography>
                            {form.bannerUrl ? (
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                    {/* Events store the RAW relative path, so the preview
                                        needs resolving like every other uploaded file.
                                        (user 2026-10-19) */}
                                    <Box component="img" src={fileUrl(form.bannerUrl)} alt="Banner"
                                        sx={{ maxWidth: '100%', maxHeight: 150, borderRadius: '10px', display: 'block', border: '1px solid #E2E8F0' }} />
                                    <Button size="small" onClick={() => set({ bannerUrl: '' })}
                                        sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.74rem', color: '#DC2626', mt: 0.5 }}>
                                        Remove banner
                                    </Button>
                                </Box>
                            ) : (
                                <Button variant="outlined" startIcon={<CloudUploadIcon />}
                                    disabled={uploading === 'banner'}
                                    onClick={() => bannerRef.current?.click()}
                                    sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px' }}>
                                    {uploading === 'banner' ? 'Uploading…' : 'Upload a banner'}
                                </Button>
                            )}
                            <input ref={bannerRef} type="file" hidden accept="image/*"
                                onChange={e => { onBanner(e.target.files?.[0]); e.target.value = ''; }} />
                        </Box>

                        <Box>
                            <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', fontWeight: 600, color: '#334155', mb: 0.75 }}>
                                Material
                            </Typography>
                            <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#64748B', mb: 1 }}>
                                PDFs, slides, checklists — whatever people should take away. Under 20 MB each.
                            </Typography>
                            <Stack spacing={0.75} sx={{ mb: 1 }}>
                                {material.map((a, i) => (
                                    <Stack key={i} direction="row" alignItems="center" spacing={1}
                                        sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', px: 1.25, py: 0.75 }}>
                                        <InsertDriveFileIcon sx={{ fontSize: 17, color: '#64748B' }} />
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', flex: 1, minWidth: 0,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {a.name || a.url}
                                        </Typography>
                                        <IconButton size="small" onClick={() => setMaterial(material.filter((_, j) => j !== i))}>
                                            <DeleteOutlineIcon sx={{ fontSize: 17, color: '#DC2626' }} />
                                        </IconButton>
                                    </Stack>
                                ))}
                            </Stack>
                            <Button variant="outlined" startIcon={<CloudUploadIcon />}
                                disabled={uploading === 'material'}
                                onClick={() => materialRef.current?.click()}
                                sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px' }}>
                                {uploading === 'material' ? 'Uploading…' : 'Upload a file'}
                            </Button>
                            <input ref={materialRef} type="file" hidden
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.zip,.csv,.txt"
                                onChange={e => { onMaterial(e.target.files?.[0]); e.target.value = ''; }} />
                        </Box>

                        <FormControlLabel
                            control={<Checkbox checked={form.showAttendeeNames} onChange={e => set({ showAttendeeNames: e.target.checked })} />}
                            label={<Typography sx={{ fontFamily: PP, fontSize: '0.84rem' }}>Show who has joined</Typography>} />
                    </Stack>
                </Paper>
            </Box>

            {/* ── Attendance — edit only ─────────────────────────────── */}
            {isEdit && (
                <Paper id="sessions" elevation={0} sx={{ ...cardSx, mt: 2, scrollMarginTop: '80px' }}>
                    {/* "Attendance" undersold it: this is the only place every session of a
                        series is listed, past and upcoming, and somebody looking for the dates
                        had no reason to open a panel with that name. The id is the anchor the
                        Next column on the events table jumps to. (user 2026-10-19) */}
                    <Typography sx={cardHeadSx}>Sessions &amp; Attendance</Typography>
                    <EventAttendance
                        eventId={Number(id)}
                        sessions={serverSessions}
                        isOnline={form.isOnline}
                    />
                </Paper>
            )}

            {/* actions */}
            <Stack direction="row" spacing={1.5} sx={{ mt: 3, flexWrap: 'wrap', gap: 1 }}>
                <Button variant="outlined" onClick={() => navigate('/events/manage')}
                    sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px' }}>
                    Cancel
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" startIcon={<SaveIcon />} disabled={saving} onClick={() => save()}
                    sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}>
                    {saving ? 'Saving…' : 'Save as draft'}
                </Button>
                <Button variant="contained" startIcon={<PublishIcon />} disabled={saving}
                    onClick={() => save({ thenPublish: true })}
                    sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}>
                    {saving ? 'Working…' : 'Save & publish'}
                </Button>
            </Stack>
            <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#64748B', mt: 1, textAlign: 'right' }}>
                Publishing puts it on the noticeboard, rings the bell for everyone it reaches, and announces
                it to the Telegram group.
            </Typography>
        </Box>
    );
}
